<template>
  <div>
    <v-radio-group v-model="localLimited" hide-details :inline="inline" class="mb-4">
      <v-radio :value="false" :class="inline ? 'mr-3' : 'mb-2'">
        <template #label>
          <span class="body1">{{ $t("modals.editWorkspace.userDeviceLimits.unlimited") }}</span>
        </template>
      </v-radio>
      <v-radio :value="true">
        <template #label>
          <div v-if="!inline">
            <div class="body1 mt-5">{{ $t("modals.editWorkspace.userDeviceLimits.limited") }}</div>
            <div class="caption text-medium-emphasis">
              {{ $t("modals.editWorkspace.userDeviceLimits.limitedHelperText") }}
            </div>
          </div>
          <span v-else class="body1">{{
            $t("modals.editWorkspace.userDeviceLimits.limited")
          }}</span>
        </template>
      </v-radio>
    </v-radio-group>
    <template v-if="localLimited">
      <div :class="inline ? 'd-flex' : ''">
        <v-text-field
          ref="protectedUsersField"
          v-model.number="numberOfProtectedUsers"
          type="number"
          :class="[inline ? 'mr-4 flex-sm-1-1-0' : 'mt-3 w-100']"
          variant="outlined"
          :min="0"
          data-testid="number-of-protected-users-field"
          :label="numberOfProtectedUsersLabel"
          :placeholder="$t('modals.editWorkspace.userDeviceLimits.numberOfProtectedUsers')"
          :rules="protectedUsersRules"
        >
          <template #message="{ message }">
            <div class="d-flex align-center">
              <v-icon
                icon="$infoXs"
                :color="isUsersRequiredError ? 'red-dark' : undefined"
                :class="['mr-1', 'flex-shrink-0', { 'mt-1': !isUsersRequiredError }]"
                size="16"
              />
              <span>{{ message }}</span>
            </div>
          </template>
        </v-text-field>
        <v-text-field
          ref="protectedDevicesField"
          v-model.number="numberOfProtectedDevices"
          type="number"
          :class="[inline ? 'flex-sm-1-1-0' : 'mt-3 w-100', 'devices-field']"
          variant="outlined"
          persistent-hint
          :min="0"
          data-testid="number-of-protected-devices-field"
          :label="numberOfProtectedDevicesLabel"
          :placeholder="$t('modals.editWorkspace.userDeviceLimits.numberOfProtectedDevices')"
          :hint="$t('modals.editWorkspace.userDeviceLimits.numberOfProtectedDevicesHint')"
          :rules="protectedDevicesRules"
        >
          <template #message="{ message }">
            <div class="d-flex align-center">
              <v-icon
                icon="$infoXs"
                :color="isDevicesRequiredError ? 'red-dark' : undefined"
                class="mr-1"
                size="16"
              />
              <span>{{ message }}</span>
            </div>
          </template>
        </v-text-field>
      </div>
      <v-text-field
        v-model.number="overCapacityAllowance"
        type="number"
        class="mt-3"
        variant="outlined"
        persistent-hint
        data-testid="over-capacity-allowance-field"
        :min="0"
        :max="100"
        :label="overCapacityAllowanceLabel"
        :placeholder="$t('modals.editWorkspace.userDeviceLimits.overCapacityAllowance')"
        :hint="$t('modals.editWorkspace.userDeviceLimits.overCapacityAllowanceHint')"
        :rules="overCapacityAllowanceRules"
      >
        <template #message="{ message }">
          <div class="d-flex align-center">
            <v-icon icon="$infoXs" class="mr-1" size="16" />
            <span>{{ message }}</span>
          </div>
        </template>
      </v-text-field>
      <v-alert :closable="false" icon="$infoXs" color="yellow-faint" class="mt-4 text-yellow-base">
        <div class="subtitle1 text-yellow-base">
          {{ $t("modals.editWorkspace.userDeviceLimits.summaryTitle") }}
        </div>
        <ul class="mt-2 bulleted-list body2 text-yellow-base">
          <li
            v-html="$t('modals.editWorkspace.userDeviceLimits.totalMaxUsers', { n: totalMaxUsers })"
          ></li>
          <li
            v-html="
              $t('modals.editWorkspace.userDeviceLimits.totalMaxDevices', { n: totalMaxDevices })
            "
          ></li>
        </ul>
      </v-alert>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { numberRequired, onlyInteger } from "@/_helpers/validators";

export interface WorkspaceLimits {
  numberOfProtectedUsers?: number;
  numberOfProtectedDevices?: number;
  overCapacityAllowance?: number;
}

export default defineComponent({
  props: {
    limited: {
      type: Boolean,
      default: false,
    },
    limits: {
      type: Object as PropType<WorkspaceLimits>,
      default: undefined,
    },
    originalProtectedUsers: {
      type: Number,
      default: undefined,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:limited", "update:limits"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const protectedUsersField = ref();
    const protectedDevicesField = ref();

    const localLimited = computed({
      get: () => props.limited,
      set: (val: boolean) => {
        emit("update:limited", val);
        if (!val) {
          emit("update:limits", undefined);
        }
      },
    });

    const numberOfProtectedUsers = computed({
      get: () => props.limits?.numberOfProtectedUsers,
      set: (val: number | undefined) =>
        emit("update:limits", { ...(props.limits ?? {}), numberOfProtectedUsers: val }),
    });

    const numberOfProtectedDevices = computed({
      get: () => props.limits?.numberOfProtectedDevices,
      set: (val: number | undefined) =>
        emit("update:limits", { ...(props.limits ?? {}), numberOfProtectedDevices: val }),
    });

    const overCapacityAllowance = computed({
      get: () =>
        props.limits?.overCapacityAllowance !== undefined
          ? Math.round(props.limits.overCapacityAllowance * 100)
          : undefined,
      set: (val: number | undefined) =>
        emit("update:limits", {
          ...(props.limits ?? {}),
          overCapacityAllowance: val !== undefined ? val / 100 : val,
        }),
    });

    const hasValidation = computed(() => props.originalProtectedUsers !== undefined);

    const numberOfProtectedUsersLabel = computed(() =>
      props.limits?.numberOfProtectedUsers != null
        ? t("modals.editWorkspace.userDeviceLimits.numberOfProtectedUsers")
        : undefined
    );

    const numberOfProtectedDevicesLabel = computed(() =>
      props.limits?.numberOfProtectedDevices != null
        ? t("modals.editWorkspace.userDeviceLimits.numberOfProtectedDevices")
        : undefined
    );

    const overCapacityAllowanceLabel = computed(() =>
      props.limits?.overCapacityAllowance != null
        ? t("modals.editWorkspace.userDeviceLimits.overCapacityAllowance")
        : undefined
    );

    const totalMaxUsers = computed(() => {
      const users = props.limits?.numberOfProtectedUsers ?? 0;
      const cap = props.limits?.overCapacityAllowance ?? 0;
      return Math.round(users * (1 + cap));
    });

    const totalMaxDevices = computed(() => {
      const devices = props.limits?.numberOfProtectedDevices ?? 0;
      const cap = props.limits?.overCapacityAllowance ?? 0;
      return Math.round(devices * (1 + cap));
    });

    const protectedUsersRules = computed(() => {
      const rules = [];
      if (localLimited.value) {
        rules.push(numberRequired());
        rules.push(onlyInteger());
      }
      if (hasValidation.value) {
        rules.push((value: number | undefined) => {
          if (!value) return true;
          const current = props.originalProtectedUsers ?? 0;
          const cap = props.limits?.overCapacityAllowance;
          const total = cap ? Math.round(value * (1 + cap)) : value;
          if (current <= total) return true;
          const leftBehind = current - total;
          if (cap) {
            return t("modals.editWorkspace.userDeviceLimits.protectedUsersValidationWithCap", {
              current,
              limit: value,
              cap: Math.round(cap * 100),
              total,
              leftBehind,
            });
          }
          return t("modals.editWorkspace.userDeviceLimits.protectedUsersValidationWithoutCap", {
            current,
            limit: value,
            leftBehind,
          });
        });
      }
      return rules;
    });

    const protectedDevicesRules = computed(() => {
      if (!localLimited.value) return [];
      return [numberRequired(), onlyInteger()];
    });

    const overCapacityAllowanceRules = [
      (value: number | undefined) => {
        if (!value) return true;
        return onlyInteger()(value!);
      },
    ];

    const isUsersRequiredError = computed(() =>
      protectedUsersRules.value.some(
        (rule) => rule(numberOfProtectedUsers.value as number) !== true
      )
    );

    const isDevicesRequiredError = computed(
      () => localLimited.value && !numberOfProtectedDevices.value
    );

    watch(
      () => props.limits?.overCapacityAllowance,
      () => {
        protectedUsersField.value?.validate();
      }
    );

    return {
      localLimited,
      numberOfProtectedUsers,
      numberOfProtectedDevices,
      overCapacityAllowance,
      hasValidation,
      numberOfProtectedUsersLabel,
      numberOfProtectedDevicesLabel,
      overCapacityAllowanceLabel,
      totalMaxUsers,
      totalMaxDevices,
      protectedUsersRules,
      protectedDevicesRules,
      overCapacityAllowanceRules,
      isUsersRequiredError,
      isDevicesRequiredError,
      protectedUsersField,
      protectedDevicesField,
    };
  },
});
</script>

<style lang="scss" scoped>
.bulleted-list {
  list-style-type: disc;
  padding-left: 1rem;
}

:deep(.bg-yellow-faint) {
  .icon-info-xs::before {
    color: rgb(var(--v-theme-yellow-base));
  }
}

.devices-field {
  :deep(.v-input__details) {
    overflow: visible;
  }
}

.v-input--error {
  :deep(input::placeholder) {
    color: rgb(var(--v-theme-red-dark)) !important;
    opacity: 1 !important;
  }

  .icon-info-xs::before {
    color: rgb(var(--v-red-dark));
  }
}
</style>
